import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, Collapse, CircularProgress, Alert, LinearProgress,
  IconButton, Tooltip, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, List, ListItem, ListItemText,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  BarChart as BarChartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Insights as InsightsIcon,
  NotificationsActive as FollowUpIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  listSalesFollowups,
  createSalesFollowup,
  deleteSalesFollowup,
  exportSalesPipelineXlsx,
} from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtCurrency(val) {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtRelative(d) {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? 's' : ''} ago`;
}

// ─── Follow-Up Logic ──────────────────────────────────────────────────────────
const STALE_DAYS = 14;

function daysSince(d) {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
}

/**
 * Returns 'urgent' | 'pending' | null
 *   urgent  → Released to Client AND stale (> 14 days)
 *   pending → Ready for Client Release OR On HOLD (any staleness)
 *
 * Locked with Tom 2026-04-28: a locally-recorded follow-up date overrides the
 * D-Tools updatedOn for staleness. If the rep logged a follow-up within the
 * last STALE_DAYS, the deal is no longer 'urgent' regardless of D-Tools.
 */
function lastTouchDate(deal) {
  // Most recent of D-Tools updatedOn and our locally-logged follow-up date.
  const dt = deal.updatedOn ? new Date(deal.updatedOn).getTime() : 0;
  const fu = deal.lastFollowupAt ? new Date(deal.lastFollowupAt).getTime() : 0;
  if (!dt && !fu) return null;
  return new Date(Math.max(dt, fu));
}

function followUpStatus(deal) {
  const progress = deal.progress;
  const lastTouch = lastTouchDate(deal);
  const stale = lastTouch ? Math.floor((Date.now() - lastTouch.getTime()) / 86400000) > STALE_DAYS : true;

  if (progress === 'Released to Client' && stale) return 'urgent';
  if (progress === 'Ready for Client Release' || progress === 'On HOLD') return 'pending';
  return null;
}

function needsFollowUp(deal) {
  return followUpStatus(deal) !== null;
}

// ─── Status Config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  'Salesperson Prep':        { color: '#757575', bg: '#f5f5f5', label: 'Salesperson Prep' },
  'Estimating / Revisions':  { color: '#1565c0', bg: '#e3f2fd', label: 'Estimating' },
  'In Financial Review':     { color: '#6a1b9a', bg: '#f3e5f5', label: 'Financial Review' },
  'Ready for Client Release':{ color: '#e65100', bg: '#fff3e0', label: 'Ready to Release' },
  'Released to Client':      { color: '#2e7d32', bg: '#e8f5e9', label: 'Released' },
  'On HOLD':                 { color: '#e65100', bg: '#fff3e0', label: 'On Hold' },
  'Rejected':                { color: '#c62828', bg: '#ffebee', label: 'Rejected' },
};

function StatusChip({ progress }) {
  const cfg = STATUS_CONFIG[progress] || { color: '#616161', bg: '#eeeeee', label: progress };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        color: cfg.color,
        bgcolor: cfg.bg,
        fontWeight: 600,
        fontSize: '0.72rem',
        height: 22,
      }}
    />
  );
}

// ─── Follow-Up Chip ───────────────────────────────────────────────────────────
function FollowUpChip({ deal }) {
  const status = followUpStatus(deal);
  if (!status) return null;

  if (status === 'urgent') {
    return (
      <Chip
        label="FOLLOW UP"
        size="small"
        sx={{
          bgcolor: '#ffebee',
          color: '#c62828',
          fontWeight: 700,
          fontSize: '0.68rem',
          height: 20,
          border: '1px solid #ef9a9a',
        }}
      />
    );
  }

  // pending
  return (
    <Chip
      label="PENDING"
      size="small"
      sx={{
        bgcolor: '#fff8e1',
        color: '#e65100',
        fontWeight: 700,
        fontSize: '0.68rem',
        height: 20,
        border: '1px solid #ffcc02',
      }}
    />
  );
}

// ─── Margin Color ─────────────────────────────────────────────────────────────
function marginColor(pct) {
  if (pct == null) return 'text.secondary';
  if (pct >= 40) return '#2e7d32';
  if (pct >= 25) return '#f57c00';
  return '#c62828';
}

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ title, value, subtitle, icon, color, active, onClick }) => (
  <Card
    elevation={active ? 4 : 2}
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '2px solid transparent',
      transition: 'border 0.15s, box-shadow 0.15s',
      '&:hover': onClick ? { boxShadow: 6 } : {},
    }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}18`, color }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight="bold" color={color}>{value}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

// ─── Follow-Up Dialog ────────────────────────────────────────────────────────
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const FollowUpDialog = ({ open, deal, onClose, onLogged }) => {
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setDate(todayIso());
      setNote('');
      setErr('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!note.trim()) {
      setErr('Add a one-line note about what you did.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const res = await createSalesFollowup(deal.id, { followup_date: date, note: note.trim() });
      if (onLogged) onLogged(res.data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Failed to log follow-up.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Log Follow-Up</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {deal && (
          <Box>
            <Typography variant="caption" color="text.secondary">Deal</Typography>
            <Typography variant="body2" fontWeight={600}>
              {deal.number ? `${deal.number} — ` : ''}{deal.name || deal.client || ''}
            </Typography>
            {deal.salesRep && (
              <Typography variant="caption" color="text.secondary">
                Sales: {deal.salesRep}
              </Typography>
            )}
          </Box>
        )}
        {err && <Alert severity="error">{err}</Alert>}
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
          required
          multiline
          rows={3}
          placeholder="e.g. Called Pat — sending revised proposal Tuesday"
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} /> : 'Log Follow-Up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Inline icon-button (used in the table row).
const LogFollowUpButton = ({ deal, onClick }) => (
  <Tooltip title="Log Follow-Up">
    <IconButton
      size="small"
      onClick={(e) => { e.stopPropagation(); onClick(deal); }}
      sx={{ color: '#2e7d32' }}
    >
      <CheckCircleIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

// History list shown in the detail panel.
const FollowUpHistory = ({ dealId, refreshKey, onLog }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSalesFollowups(dealId)
      .then((r) => { if (!cancelled) setItems(r.data?.followups || []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealId, refreshKey]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this follow-up?')) return;
    try {
      await deleteSalesFollowup(dealId, id);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to remove.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Follow-Up History
          {items.length > 0 && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({items.length})
            </Typography>
          )}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<CheckCircleIcon />}
          onClick={onLog}
          sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none' }}
        >
          Log Follow-Up
        </Button>
      </Box>
      {loading && <CircularProgress size={18} />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && items.length === 0 && (
        <Typography variant="body2" color="text.disabled">
          No follow-ups logged yet.
        </Typography>
      )}
      {items.length > 0 && (
        <List dense disablePadding>
          {items.map((it) => (
            <ListItem
              key={it.id}
              disableGutters
              sx={{ alignItems: 'flex-start', borderBottom: '1px solid #eee', py: 0.75 }}
              secondaryAction={
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => handleDelete(it.id)}>
                    <DeleteIcon fontSize="small" sx={{ color: '#c62828' }} />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {fmtDate(it.followup_date)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      — {it.completed_by_name || 'Unknown'}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {it.note}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

// ─── Expanded Detail Panel ────────────────────────────────────────────────────
const DetailPanel = ({ dealId, colSpan, onLogFollowUp, followupRefreshKey }) => {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [showFullScope, setShowFullScope] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`/api/sales-pipeline/${dealId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { setDetail(d); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [dealId]);

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: '2px solid #e3f2fd' }}>
        <Collapse in timeout="auto" unmountOnExit>
          <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
            {loading && <CircularProgress size={24} />}
            {error && <Alert severity="error" sx={{ mb: 1 }}>Failed to load details: {error}</Alert>}
            {detail && (
              <Grid container spacing={3}>
                {/* Left column: metadata */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Project Info
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <InfoRow label="Number"        value={detail.number} />
                    <InfoRow label="Client"        value={detail.client} />
                    <InfoRow label="Salesperson"   value={detail.salesRep} />
                    <InfoRow label="Project Mgr"   value={detail.projectManager} />
                    <InfoRow label="Created"       value={fmtDate(detail.createdOn)} />
                    <InfoRow label="Last Updated"  value={fmtDate(detail.updatedOn)} />
                    <InfoRow label="Est. Close"    value={fmtDate(detail.estimatedCloseDate)} />
                    <InfoRow label="Value"         value={fmtCurrency(detail.price)} />
                    {detail.margin != null && (
                      <InfoRow
                        label="Margin"
                        value={`${detail.margin.toFixed(1)}%`}
                        valueColor={marginColor(detail.margin)}
                      />
                    )}
                  </Box>
                </Grid>

                {/* Middle column: scope */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Scope of Work
                  </Typography>
                  {detail.scopeOfWork ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {showFullScope
                          ? detail.scopeOfWork
                          : detail.scopeOfWork.slice(0, 300) + (detail.scopeOfWork.length > 300 ? '…' : '')}
                      </Typography>
                      {detail.scopeOfWork.length > 300 && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ cursor: 'pointer', mt: 0.5, display: 'block' }}
                          onClick={() => setShowFullScope((v) => !v)}
                        >
                          {showFullScope ? 'Show less' : 'Read more'}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">No scope of work on file.</Typography>
                  )}
                </Grid>

                {/* Right column: items by category */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Items by Category
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({detail.itemCount} total)
                    </Typography>
                  </Typography>
                  {detail.categories && detail.categories.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {detail.categories.map((cat) => (
                        <Box
                          key={cat.category}
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <Typography variant="body2" color="text.secondary">{cat.category}</Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {Math.round(cat.count)} units
                            </Typography>
                            {cat.totalLaborHours > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                {cat.totalLaborHours.toFixed(1)}h labor
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">No items found.</Typography>
                  )}
                </Grid>

                {/* Follow-Up History (full-width row) */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <FollowUpHistory
                    dealId={dealId}
                    refreshKey={followupRefreshKey}
                    onLog={onLogFollowUp}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const InfoRow = ({ label, value, valueColor }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="caption" fontWeight={600} color={valueColor || 'text.primary'}>
      {value || '—'}
    </Typography>
  </Box>
);

// ─── Quick-Filter Chip Row ────────────────────────────────────────────────────
function QuickFilterChips({ deals, activeQuick, onSelect }) {
  const followUpCount  = useMemo(() => deals.filter(needsFollowUp).length, [deals]);
  const releasedCount  = useMemo(() => deals.filter((d) => d.progress === 'Released to Client').length, [deals]);
  const onHoldCount    = useMemo(() => deals.filter((d) => d.progress === 'On HOLD').length, [deals]);

  const chips = [
    { key: '',              label: `All Deals (${deals.length})`,          color: '#1A5490' },
    { key: 'followup',      label: `Needs Follow-Up (${followUpCount})`,   color: '#c62828' },
    { key: 'released',      label: `Released to Client (${releasedCount})`,color: '#2e7d32' },
    { key: 'onhold',        label: `On Hold (${onHoldCount})`,             color: '#e65100' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      {chips.map(({ key, label, color }) => (
        <Chip
          key={key}
          label={label}
          onClick={() => onSelect(key)}
          sx={{
            fontWeight: 600,
            fontSize: '0.8rem',
            cursor: 'pointer',
            bgcolor: activeQuick === key ? color : 'transparent',
            color: activeQuick === key ? '#fff' : color,
            border: `1.5px solid ${color}`,
            '&:hover': {
              bgcolor: activeQuick === key ? color : `${color}18`,
            },
          }}
        />
      ))}
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SalesPipeline() {
  useAuth(); // auth context — page access enforced at route level

  const [deals, setDeals]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Follow-up dialog state
  const [followupTarget, setFollowupTarget] = useState(null);
  const [followupRefreshKey, setFollowupRefreshKey] = useState(0);

  const openFollowupDialog = useCallback((deal) => {
    setFollowupTarget(deal);
  }, []);
  const closeFollowupDialog = useCallback(() => {
    setFollowupTarget(null);
  }, []);
  const handleFollowupLogged = useCallback((row) => {
    // Update the deal's lastFollowupAt in-place so the chip clears immediately
    if (followupTarget) {
      const localDate = row?.due_date
        ? new Date(row.due_date).toISOString()
        : new Date().toISOString();
      setDeals((prev) => prev.map((d) =>
        d.id === followupTarget.id
          ? { ...d, lastFollowupAt: localDate, followupCount: (d.followupCount || 0) + 1 }
          : d
      ));
    }
    setFollowupRefreshKey((k) => k + 1);
  }, [followupTarget]);

  // Filters
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [repFilter, setRepFilter]     = useState('');
  const [followUpOnly, setFollowUpOnly] = useState(false);
  const [quickFilter, setQuickFilter] = useState(''); // '' | 'followup' | 'released' | 'onhold'

  // Sort
  const [sortBy, setSortBy]     = useState('updatedOn');
  const [sortDir, setSortDir]   = useState('desc');

  // ── Load deals ──────────────────────────────────────────────────────────────
  const loadDeals = useCallback(() => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    fetch('/api/sales-pipeline', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { setDeals(d.deals || []); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  // When quick filter changes, sync the followUpOnly toggle and statusFilter
  const handleQuickFilter = useCallback((key) => {
    setQuickFilter(key);
    // Reset the secondary filters when using quick filters
    if (key === 'followup') {
      setFollowUpOnly(true);
      setStatusFilter('');
    } else if (key === 'released') {
      setFollowUpOnly(false);
      setStatusFilter('Released to Client');
    } else if (key === 'onhold') {
      setFollowUpOnly(false);
      setStatusFilter('On HOLD');
    } else {
      setFollowUpOnly(false);
      setStatusFilter('');
    }
  }, []);

  // Keep quick filter in sync when user manually changes status/followup toggle
  const handleStatusChange = useCallback((val) => {
    setStatusFilter(val);
    setFollowUpOnly(false);
    setQuickFilter(
      val === 'Released to Client' ? 'released'
      : val === 'On HOLD' ? 'onhold'
      : ''
    );
  }, []);

  const handleFollowUpToggle = useCallback((checked) => {
    setFollowUpOnly(checked);
    setQuickFilter(checked ? 'followup' : '');
    if (checked) setStatusFilter('');
  }, []);

  // ── Unique reps for dropdown ─────────────────────────────────────────────
  const uniqueReps = useMemo(() => {
    const reps = [...new Set(deals.map((d) => d.salesRep).filter(Boolean))].sort();
    return reps;
  }, [deals]);

  // ── Filtered + Sorted ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = deals;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (d) =>
          d.name?.toLowerCase().includes(q) ||
          d.client?.toLowerCase().includes(q) ||
          d.number?.toLowerCase().includes(q)
      );
    }
    if (followUpOnly) {
      list = list.filter(needsFollowUp);
    } else if (statusFilter) {
      list = list.filter((d) => d.progress === statusFilter);
    }
    if (repFilter) {
      list = list.filter((d) => d.salesRep === repFilter);
    }

    // Sort
    list = [...list].sort((a, b) => {
      let va = a[sortBy];
      let vb = b[sortBy];

      if (sortBy === 'price') {
        va = va != null ? parseFloat(va) : -Infinity;
        vb = vb != null ? parseFloat(vb) : -Infinity;
      } else if (sortBy === 'updatedOn' || sortBy === 'createdOn' || sortBy === 'publishedOn') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      } else {
        va = va ?? '';
        vb = vb ?? '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
      }

      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [deals, search, statusFilter, repFilter, followUpOnly, sortBy, sortDir]);

  // ── Summary Metrics ──────────────────────────────────────────────────────
  const totalValue = useMemo(
    () => filtered.reduce((s, d) => s + (d.price != null ? parseFloat(d.price) : 0), 0),
    [filtered]
  );

  const followUpCount = useMemo(
    () => deals.filter(needsFollowUp).length,
    [deals]
  );

  // ── Sort handler ─────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const SortCell = ({ col, children, align }) => (
    <TableCell align={align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>
      <TableSortLabel
        active={sortBy === col}
        direction={sortBy === col ? sortDir : 'asc'}
        onClick={() => handleSort(col)}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  // ── Status filter labels ─────────────────────────────────────────────────
  const STATUS_OPTIONS = [
    'Salesperson Prep',
    'Estimating / Revisions',
    'In Financial Review',
    'Ready for Client Release',
    'Released to Client',
    'On HOLD',
    'Rejected',
  ];

  // Column count (for colSpan) — 12 (added Action column for log-follow-up)
  const COL_COUNT = 12;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>Sales Pipeline</Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            {filtered.length} active deal{filtered.length !== 1 ? 's' : ''}{' '}
            &bull; {fmtCurrency(totalValue)} total pipeline
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            component={RouterLink}
            to="/sales-pipeline/forecast"
            variant="outlined"
            size="small"
            startIcon={<InsightsIcon />}
            sx={{ textTransform: 'none' }}
          >
            Forecast
          </Button>
          <Tooltip title="Download as Excel (sortable in Excel)">
            <span>
              <IconButton
                onClick={async () => {
                  try {
                    const res = await exportSalesPipelineXlsx();
                    const blob = new Blob([res.data], {
                      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const stamp = new Date().toISOString().slice(0, 10);
                    a.download = `ACS_Sales_Pipeline_${stamp}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error('Pipeline export failed:', e);
                    alert('Failed to export pipeline. Please try again.');
                  }
                }}
                disabled={loading}
              >
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refresh (clears 5-min cache after server restart)">
            <IconButton onClick={loadDeals} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Summary Cards ────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Deals"
            value={filtered.length}
            subtitle="active pipeline"
            icon={<BarChartIcon />}
            color="#1A5490"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Pipeline Value"
            value={fmtCurrency(totalValue)}
            subtitle="across all active deals"
            icon={<AttachMoneyIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Avg Deal Size"
            value={filtered.length > 0 ? fmtCurrency(totalValue / filtered.length) : '—'}
            subtitle="per active deal"
            icon={<TrendingUpIcon />}
            color="#6a1b9a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Needs Follow-Up"
            value={followUpCount}
            subtitle="released or stalled deals"
            icon={<FollowUpIcon />}
            color="#c62828"
            active={quickFilter === 'followup'}
            onClick={() => handleQuickFilter(quickFilter === 'followup' ? '' : 'followup')}
          />
        </Grid>
      </Grid>

      {/* ── Quick Filter Chips ────────────────────────────────────────────── */}
      <QuickFilterChips
        deals={deals}
        activeQuick={quickFilter}
        onSelect={handleQuickFilter}
      />

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search"
          size="small"
          placeholder="Name, client, job #…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                <StatusChip progress={s} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Salesperson</InputLabel>
          <Select
            value={repFilter}
            label="Salesperson"
            onChange={(e) => setRepFilter(e.target.value)}
          >
            <MenuItem value="">All Salespeople</MenuItem>
            {uniqueReps.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={followUpOnly}
              onChange={(e) => handleFollowUpToggle(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#c62828' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#c62828' },
              }}
            />
          }
          label={
            <Typography variant="body2" fontWeight={followUpOnly ? 700 : 400} color={followUpOnly ? '#c62828' : 'text.secondary'}>
              Needs Follow-Up only
            </Typography>
          }
        />
      </Box>

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pipeline data: {error}
        </Alert>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <Paper variant="outlined">
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <SortCell col="number">Job #</SortCell>
                <SortCell col="name">Project Name</SortCell>
                <SortCell col="client">Client</SortCell>
                <SortCell col="salesRep">Salesperson</SortCell>
                <SortCell col="price" align="right">Value</SortCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Margin</TableCell>
                <SortCell col="progress">Status</SortCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Follow-Up</TableCell>
                <SortCell col="updatedOn">Last Touch</SortCell>
                <SortCell col="createdOn">Created</SortCell>
                <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 700 }} align="center">Action</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No pipeline deals match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((deal) => {
                const isExpanded = expandedId === deal.id;
                const fuStatus   = followUpStatus(deal);
                const rowBg      = fuStatus === 'urgent'  ? '#fff5f5'
                                 : fuStatus === 'pending' ? '#fffde7'
                                 : undefined;
                const rowBgHover = fuStatus === 'urgent'  ? '#ffe8e8'
                                 : fuStatus === 'pending' ? '#fff9c4'
                                 : undefined;
                return (
                  <React.Fragment key={deal.id}>
                    <TableRow
                      hover
                      onClick={() => setExpandedId(isExpanded ? null : deal.id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isExpanded ? '#e3f2fd' : rowBg,
                        '&:hover': { bgcolor: isExpanded ? '#bbdefb' : rowBgHover },
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                        {deal.number || '—'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, maxWidth: 240 }}>
                        <Tooltip title={deal.name} placement="top">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 230,
                            }}
                          >
                            {deal.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {deal.client || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {deal.salesRep || <em style={{ color: '#9e9e9e' }}>Unknown</em>}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {fmtCurrency(deal.price)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* Margin not on list endpoint — loaded on expand */}
                        <Typography variant="caption" color="text.disabled">
                          {isExpanded ? 'see below' : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip progress={deal.progress} />
                      </TableCell>
                      <TableCell>
                        <FollowUpChip deal={deal} />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const lt = lastTouchDate(deal);
                          const usingFollowup = deal.lastFollowupAt &&
                            (!deal.updatedOn || new Date(deal.lastFollowupAt) >= new Date(deal.updatedOn));
                          return (
                            <Tooltip
                              title={
                                usingFollowup
                                  ? `Last follow-up logged ${fmtDate(deal.lastFollowupAt)} (${deal.followupCount || 0} total)`
                                  : `D-Tools updated ${fmtDate(deal.updatedOn)}`
                              }
                              placement="top"
                            >
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {lt ? fmtRelative(lt) : '—'}
                                </Typography>
                                {usingFollowup && (
                                  <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fmtDate(deal.createdOn)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: 48 }} onClick={(e) => e.stopPropagation()}>
                        <LogFollowUpButton deal={deal} onClick={openFollowupDialog} />
                      </TableCell>
                      <TableCell sx={{ width: 40 }}>
                        {isExpanded
                          ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        }
                      </TableCell>
                    </TableRow>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <DetailPanel
                        dealId={deal.id}
                        colSpan={COL_COUNT}
                        onLogFollowUp={() => openFollowupDialog(deal)}
                        followupRefreshKey={followupRefreshKey}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Follow-Up Logging Dialog */}
      <FollowUpDialog
        open={!!followupTarget}
        deal={followupTarget}
        onClose={closeFollowupDialog}
        onLogged={handleFollowupLogged}
      />
    </Box>
  );
}
